import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * AdminErrorState (iter186) — shared fallback UI for admin cards when
 * their backend endpoint fails. Previously each card either:
 *   • spammed a red sonner toast on every 30-s auto-refresh, OR
 *   • silently kept spinning with no explanation
 *
 * Both are hostile to a non-technical operator. This component reads
 * an axios-shaped error and shows a calm, single-line explanation with
 * a Retry button. Common causes are surfaced as plain English rather
 * than 520 / ECONNREFUSED / etc.
 */
export default function AdminErrorState({ error, onRetry, label = "this panel" }) {
  const status = error?.response?.status;
  let human = "Can't load right now.";
  if (status === 401 || status === 403) human = "Your admin session expired. Sign in again.";
  else if (status === 429) human = "We hit a rate limit — this'll clear in a minute.";
  else if (status >= 500 && status < 600) human = "Backend is having a moment. Usually resolves within 30 seconds.";
  else if (status === 520 || status === 522 || status === 524) human = "Cloudflare can't reach the app right now (usually a deploy in progress).";
  else if (!error?.response) human = "Network glitch — check your signal, then retry.";

  return (
    <div className="border hairline p-3 bg-[#fff4ee] text-[#7f1d1d] text-[11px] flex items-start gap-2" data-testid="admin-error-state">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-bold leading-tight">Can&rsquo;t load {label}</div>
        <div className="mono text-[10px] mt-0.5 leading-tight">{human}</div>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] mono uppercase tracking-wider border hairline bg-white hover:border-[#ff4500] hover:bg-[#fff]"
          data-testid="admin-error-retry"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      )}
    </div>
  );
}
